import { useEffect, useState, type FormEvent } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { Archive, ImageUp, PackagePlus, Pencil, Plus, RotateCcw, X } from 'lucide-react'
import Modal from './Modal'
import ProductImageCropper from './ProductImageCropper'
import { formatPrice, type ProductVariant, type ShopProduct } from '../types/Shop'

type AdminProduct = Omit<ShopProduct, 'memberPriceEligible'>
type VariantForm = Pick<ProductVariant, 'label' | 'stock' | 'isActive' | 'updatedAt'> & { id?: number }

interface ProductForm {
  name: string
  description: string
  imageUrl: string
  memberPrice: string
  publicPrice: string
  isActive: boolean
  variants: VariantForm[]
}

const emptyForm = (): ProductForm => ({
  name: '',
  description: '',
  imageUrl: '',
  memberPrice: '',
  publicPrice: '',
  isActive: true,
  variants: [{ label: 'Unique', stock: 0, isActive: true }],
})

const ShopProductsAdminModule = () => {
  const [products, setProducts] = useState<AdminProduct[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState<AdminProduct | null>(null)
  const [form, setForm] = useState<ProductForm>(emptyForm)
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [imageToCrop, setImageToCrop] = useState<File | null>(null)

  const loadProducts = async () => {
    try {
      const { data } = await axios.get<AdminProduct[]>('/api/shop/admin/products')
      setProducts(data)
    } catch {
      toast.error('Impossible de charger les produits')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void loadProducts() }, [])

  const openCreate = () => {
    setEditing(null)
    setForm(emptyForm())
    setShowForm(true)
  }

  const openEdit = (product: AdminProduct) => {
    setEditing(product)
    setForm({
      name: product.name,
      description: product.description,
      imageUrl: product.imageUrl,
      memberPrice: (product.memberPriceCents / 100).toFixed(2),
      publicPrice: (product.publicPriceCents / 100).toFixed(2),
      isActive: product.isActive,
      variants: product.variants.map(({ id, label, stock, isActive, updatedAt }) => ({ id, label, stock, isActive, updatedAt })),
    })
    setShowForm(true)
  }

  const deleteOrphan = async (imagePath: string) => {
    try {
      await axios.delete('/api/upload', { data: { path: imagePath } })
    } catch (error) {
      if (!axios.isAxiosError(error) || ![404, 409].includes(error.response?.status ?? 0)) {
        console.error('Impossible de nettoyer la photo produit temporaire :', error)
      }
    }
  }

  const closeForm = async () => {
    if (saving || uploading) return
    if (form.imageUrl && form.imageUrl !== editing?.imageUrl) await deleteOrphan(form.imageUrl)
    setShowForm(false)
  }

  const uploadImage = async (file: File) => {
    setImageToCrop(null)
    setUploading(true)
    try {
      const payload = new FormData()
      payload.append('productImage', file)
      const { data } = await axios.post<{ imageUrl: string }>('/api/upload/product', payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      if (form.imageUrl && form.imageUrl !== editing?.imageUrl) await deleteOrphan(form.imageUrl)
      setForm((current) => ({ ...current, imageUrl: data.imageUrl }))
      toast.success('Photo ajoutée')
    } catch (error) {
      toast.error(axios.isAxiosError(error) ? error.response?.data?.message || 'Échec de l’upload' : 'Échec de l’upload')
    } finally {
      setUploading(false)
    }
  }

  const saveProduct = async (event: FormEvent) => {
    event.preventDefault()
    if (!form.imageUrl) return toast.error('Ajoutez une photo du produit')
    const memberPriceCents = Math.round(Number.parseFloat(form.memberPrice.replace(',', '.')) * 100)
    const publicPriceCents = Math.round(Number.parseFloat(form.publicPrice.replace(',', '.')) * 100)
    if (!Number.isFinite(memberPriceCents) || !Number.isFinite(publicPriceCents)) return toast.error('Les prix sont invalides')

    setSaving(true)
    try {
      const payload = {
        name: form.name,
        description: form.description,
        imageUrl: form.imageUrl,
        memberPriceCents,
        publicPriceCents,
        isActive: form.isActive,
        variants: form.variants,
      }
      const request = editing
        ? axios.put(`/api/shop/admin/products/${editing.id}`, payload)
        : axios.post('/api/shop/admin/products', payload)
      await request
      toast.success(editing ? 'Produit mis à jour' : 'Produit créé')
      setShowForm(false)
      await loadProducts()
    } catch (error) {
      toast.error(axios.isAxiosError(error) ? error.response?.data?.message || 'Impossible d’enregistrer le produit' : 'Impossible d’enregistrer le produit')
    } finally {
      setSaving(false)
    }
  }

  const toggleArchive = async (product: AdminProduct) => {
    try {
      await axios.patch(`/api/shop/admin/products/${product.id}/archive`, { isActive: !product.isActive })
      toast.success(product.isActive ? 'Produit archivé' : 'Produit réactivé')
      await loadProducts()
    } catch {
      toast.error('Impossible de modifier le produit')
    }
  }

  const updateVariant = (index: number, patch: Partial<VariantForm>) => setForm((current) => ({
    ...current,
    variants: current.variants.map((variant, variantIndex) => variantIndex === index ? { ...variant, ...patch } : variant),
  }))

  return (
    <div className="space-y-6">
      <div className="admin-module-toolbar">
        <div><p className="text-sm text-gray-600">Gérez les prix, tailles et stocks disponibles.</p></div>
        <button type="button" className="btn-primary" onClick={openCreate}><PackagePlus className="h-4 w-4" /> Nouveau produit</button>
      </div>

      {loading ? <div className="shop-orders-loading">Chargement du catalogue…</div> : products.length === 0 ? (
        <div className="empty-state"><PackagePlus className="mx-auto mb-4 h-10 w-10 text-emerald-600" /><h3>Aucun produit</h3><p className="mt-2 text-gray-600">Créez le premier article de la boutique.</p></div>
      ) : (
        <div className="admin-product-grid">
          {products.map((product) => (
            <article className={`admin-product-card ${!product.isActive ? 'admin-product-card-archived' : ''}`} key={product.id}>
              <img src={product.imageUrl} alt="" />
              <div className="admin-product-copy">
                <div className="flex items-start justify-between gap-3">
                  <div><small>{product.isActive ? 'En boutique' : 'Archivé'}</small><h3>{product.name}</h3></div>
                  <span>{product.variants.reduce((sum, variant) => sum + variant.stock, 0)} en stock</span>
                </div>
                <p>{product.variants.map((variant) => `${variant.label}: ${variant.stock}`).join(' · ')}</p>
                <div className="admin-product-prices"><strong>{formatPrice(product.publicPriceCents)} public</strong><strong>{formatPrice(product.memberPriceCents)} adhérent</strong></div>
                <div className="flex flex-wrap gap-2">
                  <button type="button" className="btn-secondary" onClick={() => openEdit(product)}><Pencil className="h-4 w-4" /> Modifier</button>
                  <button type="button" className="btn-secondary" onClick={() => void toggleArchive(product)}>{product.isActive ? <Archive className="h-4 w-4" /> : <RotateCcw className="h-4 w-4" />}{product.isActive ? 'Archiver' : 'Réactiver'}</button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      <Modal
        isOpen={showForm && imageToCrop === null}
        onClose={() => void closeForm()}
        title={editing ? 'Modifier le produit' : 'Nouveau produit'}
        eyebrow="Catalogue boutique"
        size="lg"
        footer={(
          <>
            <button type="button" className="btn-secondary" onClick={() => void closeForm()} disabled={saving || uploading}>Annuler</button>
            <button type="submit" form="shop-product-form" className="btn-primary" disabled={saving || uploading}>{saving ? 'Enregistrement…' : 'Enregistrer'}</button>
          </>
        )}
      >
        <form id="shop-product-form" className="space-y-5" onSubmit={saveProduct}>
          <section className="form-section">
            <div className="form-section-heading"><div><h3>Photo du produit</h3><p>Choisissez une image, puis ajustez manuellement son cadrage.</p></div></div>
            <div className="shop-admin-image-field">
            {form.imageUrl ? <img src={form.imageUrl} alt="Aperçu du produit" /> : <span><ImageUp /><small>Photo requise</small></span>}
            <label className="btn-secondary cursor-pointer">
              <ImageUp className="h-4 w-4" /> {uploading ? 'Envoi…' : 'Choisir une photo'}
              <input
                className="sr-only"
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                disabled={uploading}
                onChange={(event) => {
                  const file = event.target.files?.[0]
                  event.target.value = ''
                  if (file) setImageToCrop(file)
                }}
              />
            </label>
            </div>
          </section>

          <div className="shop-form-grid !p-0">
            <label className="form-field sm:col-span-2" htmlFor="product-name"><span className="form-label">Nom</span><input id="product-name" className="input-field" required minLength={2} placeholder="Ex. T-shirt du club" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} /></label>
            <label className="form-field sm:col-span-2" htmlFor="product-description"><span className="form-label">Description</span><textarea id="product-description" className="input-field" required minLength={10} rows={4} placeholder="Décrivez la matière, la coupe ou l’usage du produit…" value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} /></label>
            <label className="form-field" htmlFor="product-member-price"><span className="form-label">Prix adhérent (€)</span><input id="product-member-price" className="input-field" required inputMode="decimal" placeholder="0,00" value={form.memberPrice} onChange={(event) => setForm({ ...form, memberPrice: event.target.value })} /></label>
            <label className="form-field" htmlFor="product-public-price"><span className="form-label">Prix public (€)</span><input id="product-public-price" className="input-field" required inputMode="decimal" placeholder="0,00" value={form.publicPrice} onChange={(event) => setForm({ ...form, publicPrice: event.target.value })} /></label>
          </div>

          <div className="shop-variant-editor">
            <div className="flex items-center justify-between gap-3"><div><p className="eyebrow !mb-1">Tailles</p><h3>Stock disponible</h3></div><button type="button" className="btn-secondary" onClick={() => setForm((current) => ({ ...current, variants: [...current.variants, { label: '', stock: 0, isActive: true }] }))}><Plus className="h-4 w-4" /> Ajouter</button></div>
            <div className="space-y-3">
              {form.variants.map((variant, index) => (
                <div className="shop-variant-row" key={variant.id ?? `new-${index}`}>
                  <label className="form-field"><span className="form-label">Libellé</span><input className="input-field" required placeholder="Ex. M" value={variant.label} onChange={(event) => updateVariant(index, { label: event.target.value })} /></label>
                  <label className="form-field"><span className="form-label">Stock</span><input className="input-field" type="number" required min={0} value={variant.stock} onChange={(event) => updateVariant(index, { stock: Number(event.target.value) })} /></label>
                  <label className="shop-checkbox"><input type="checkbox" checked={variant.isActive} onChange={(event) => updateVariant(index, { isActive: event.target.checked })} /><span>Active</span></label>
                  <button type="button" className="icon-button icon-button-danger" disabled={form.variants.length === 1} onClick={() => setForm((current) => ({ ...current, variants: current.variants.filter((_, variantIndex) => variantIndex !== index) }))} aria-label="Retirer la taille"><X /></button>
                </div>
              ))}
            </div>
          </div>

          <label className="form-check-card"><input type="checkbox" checked={form.isActive} onChange={(event) => setForm({ ...form, isActive: event.target.checked })} /><span>Produit visible dans la boutique</span></label>
        </form>
      </Modal>

      <Modal
        isOpen={imageToCrop !== null}
        onClose={() => setImageToCrop(null)}
        title="Recadrer la photo"
        eyebrow="Photo du produit"
        size="lg"
      >
        {imageToCrop && (
          <ProductImageCropper
            file={imageToCrop}
            onCancel={() => setImageToCrop(null)}
            onConfirm={(file) => void uploadImage(file)}
            disabled={uploading}
          />
        )}
      </Modal>
    </div>
  )
}

export default ShopProductsAdminModule
